// GLBA-13207: visual-case graphs may be externalized to gl-file-service
// (`graphUrl` instead of inline `graph`) and the api no longer refreshes fiat
// prices server-side, so the graph source resolution and the price merge
// (ported from gl-users-api rate-utils) live here now.

const coinFamilyBTC = ['btc', 'ltc']
const COIN_DECIMALS = { btc: 8, ltc: 8 }
const MILLISECONDS_IN_SECOND = 1000

export function normalizeCaseGraph(graph) {
  if (!graph) return undefined
  return typeof graph === 'string' ? graph : JSON.stringify(graph)
}

export async function fetchExternalGraph(url, apiGet) {
  const { url: fileUrl } = await apiGet(`${url}${url.includes('?') ? '&' : '?'}as=json`)
  const response = await fetch(fileUrl)

  if (!response.ok) throw new Error(`Case graph request failed with ${response.status}`)

  return response.text()
}

// Legacy docs keep the inline `graph`; externalized docs carry `graphUrl`
// pointing at gl-file-service. Returns the graph as a string either way.
export async function resolveCaseGraph(data, fetchGraph) {
  if (!data) return undefined
  if (data.graph) return normalizeCaseGraph(data.graph)
  if (data.graphUrl) return normalizeCaseGraph(await fetchGraph(data.graphUrl))
  return undefined
}

// A multi-chain case mixes edges from different coins under one graph-level
// `searchMode`, so pricing must key off each edge's own coinData.key (every edge
// carries one — see services/cytoscape.js createEdge) — `searchMode` is only a
// fallback for edges saved before coinData was attached to every edge.
function resolveEdgeCoin(edge, fallbackCoinKey) {
  return (edge?.data?.coinData?.key || fallbackCoinKey || '').toLowerCase() || undefined
}

export function getEdgeTimestamp(edge, coinKey) {
  const coin = resolveEdgeCoin(edge, coinKey)
  const timestamp = coinFamilyBTC.includes(coin)
    ? edge?.data?.sourceTimestamp
    : edge?.data?.additionalData?.timestamp

  if (!timestamp) return undefined

  return String(timestamp).length === 10 ? timestamp * MILLISECONDS_IN_SECOND : timestamp
}

// One `/rates/get` request item per priceable edge, coined per edge (see
// resolveEdgeCoin) so a multi-chain graph requests the right rate for each one.
// Bails to [] for the old graph format (no `searchMode`) and for graphs without edges.
export function collectCaseRateRequests(visualCase, currency) {
  if (!visualCase?.searchMode || !visualCase.elements?.edges?.length) return []

  const requests = []

  visualCase.elements.edges.forEach(edge => {
    const coin = resolveEdgeCoin(edge, visualCase.searchMode)
    const timestamp = getEdgeTimestamp(edge, visualCase.searchMode)

    if (!timestamp) return

    requests.push({
      coin: coin.toUpperCase(),
      to: currency,
      timestamp,
      contract: edge?.data?.additionalData?.contractData?.address || ''
    })
  })

  return requests
}

export function calculatePrice({ amount, price, decimals }) {
  if (!amount || !price) return null

  let result = amount

  if (decimals) result = Math.abs(result / 10 ** decimals)

  return result * price
}

export function findPrice({ coin, contract, to, timestamp, rates }) {
  if (!timestamp) return undefined

  // rates are sorted asc by timestamp: scanning backwards yields the latest
  // rate at or before the edge timestamp
  for (let s = rates.length; s-- > 0;) {
    const rate = rates[s]

    if (
      rate.timestamp <= timestamp &&
      (rate.coin || '').toLowerCase() === coin &&
      (rate.contract || '') === contract &&
      rate.to === to
    ) {
      return rate.price
    }
  }

  return undefined
}

// Writes the fiat value onto `edge.data[rateProp]` for every edge, coined per edge
// (see resolveEdgeCoin) so a multi-chain graph doesn't price every edge as the same
// coin. Mirrors the deleted api-side refresh. Mutates and returns `visualCase`.
export function mergeCaseGraphPrices(visualCase, rates, currency, rateProp = 'price') {
  if (!visualCase?.searchMode || !visualCase.elements?.edges?.length) return visualCase

  const sortedRates = [...(rates || [])].sort((a, b) => a.timestamp - b.timestamp)

  visualCase.elements.edges.forEach(edge => {
    const coin = resolveEdgeCoin(edge, visualCase.searchMode)
    const decimals = coinFamilyBTC.includes(coin) ? COIN_DECIMALS[coin] : undefined

    const price = findPrice({
      coin,
      contract: edge?.data?.additionalData?.contractData?.address || '',
      to: currency,
      timestamp: getEdgeTimestamp(edge, visualCase.searchMode),
      rates: sortedRates
    })

    edge.data[rateProp] = calculatePrice({ amount: edge.data.amount, price, decimals })
  })

  return visualCase
}
